import type { ReactNode, CSSProperties } from 'react';
import { cn } from '../cn.js';
import { Skeleton } from './Skeleton.js';

export interface TableColumn<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  className?: string;
  isPinnedLeft?: boolean;
  pinnedLeftOffset?: number | string;
  isPinnedRight?: boolean;
  pinnedRightOffset?: number | string;
}

export interface TableProps<T> {
  columns: TableColumn<T>[];
  rows: T[];
  getRowId: (row: T) => string;
  isLoading?: boolean;
  emptyState?: ReactNode;
  containerClassName?: string;
  maxHeight?: string;
}

/**
 * Enhanced Data Table primitive with Horilla-style sticky pinned columns,
 * height-constrained viewport scrolling, and header alignment.
 */
export function Table<T>({
  columns,
  rows,
  getRowId,
  isLoading = false,
  emptyState,
  containerClassName,
  // dvh tracks the mobile URL bar; the floor keeps a usable table when the keyboard / a short landscape
  // viewport would otherwise squash it to nothing.
  maxHeight = 'max(20rem, calc(100dvh - 280px))',
}: TableProps<T>) {
  return (
    <div
      className={cn(
        // `isolate`: the sticky header (z-20) and pinned columns (z-30) stay inside this stacking
        // context, so they can no longer paint over the AppShell's sticky topbar (z-10) on scroll.
        'relative isolate overflow-auto rounded-xl border border-border bg-surface shadow-xs',
        containerClassName,
      )}
      style={{ maxHeight }}
    >
      <table className="w-full text-left text-sm border-collapse">
        <thead className="sticky top-0 z-20 bg-bg-subtle">
          <tr className="border-b border-border bg-bg-subtle">
            {columns.map((col) => {
              const isLeft = col.isPinnedLeft;
              const isRight = col.isPinnedRight;

              const style: CSSProperties = {};
              if (isLeft) {
                style.position = 'sticky';
                style.left = col.pinnedLeftOffset ?? 0;
                style.zIndex = 30;
              } else if (isRight) {
                style.position = 'sticky';
                style.right = col.pinnedRightOffset ?? 0;
                style.zIndex = 30;
              }

              return (
                <th
                  key={col.key}
                  style={style}
                  className={cn(
                    'h-(--control-height) px-3 py-2 text-xs font-semibold text-fg-muted bg-bg-subtle whitespace-nowrap',
                    isLeft && 'shadow-[4px_0_10px_-2px_rgba(0,0,0,0.06)] border-r border-border/50',
                    isRight &&
                      'shadow-[-4px_0_10px_-2px_rgba(0,0,0,0.06)] border-l border-border/50',
                    col.className,
                  )}
                >
                  {col.header}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {isLoading &&
            Array.from({ length: 6 }).map((_, index) => (
              <tr key={`skeleton-${index}`} className="h-(--row-height) border-b border-border">
                {columns.map((col) => {
                  const isLeft = col.isPinnedLeft;
                  const isRight = col.isPinnedRight;
                  const style: CSSProperties = {};
                  if (isLeft) {
                    style.position = 'sticky';
                    style.left = col.pinnedLeftOffset ?? 0;
                    style.zIndex = 10;
                  } else if (isRight) {
                    style.position = 'sticky';
                    style.right = col.pinnedRightOffset ?? 0;
                    style.zIndex = 10;
                  }
                  return (
                    <td
                      key={col.key}
                      style={style}
                      className={cn(
                        'px-3 py-2.5 bg-surface',
                        isLeft &&
                          'shadow-[4px_0_10px_-2px_rgba(0,0,0,0.06)] border-r border-border/50',
                        isRight &&
                          'shadow-[-4px_0_10px_-2px_rgba(0,0,0,0.06)] border-l border-border/50',
                      )}
                    >
                      <Skeleton className="h-4 w-full max-w-40" />
                    </td>
                  );
                })}
              </tr>
            ))}

          {!isLoading &&
            rows.map((row) => (
              <tr
                key={getRowId(row)}
                className="group h-(--row-height) border-b border-border last:border-b-0 hover:bg-bg-subtle/70 transition-colors"
              >
                {columns.map((col) => {
                  const isLeft = col.isPinnedLeft;
                  const isRight = col.isPinnedRight;

                  const style: CSSProperties = {};
                  if (isLeft) {
                    style.position = 'sticky';
                    style.left = col.pinnedLeftOffset ?? 0;
                    style.zIndex = 10;
                  } else if (isRight) {
                    style.position = 'sticky';
                    style.right = col.pinnedRightOffset ?? 0;
                    style.zIndex = 10;
                  }

                  return (
                    <td
                      key={col.key}
                      style={style}
                      className={cn(
                        'px-3 py-2.5 text-fg bg-surface group-hover:bg-bg-subtle/70 transition-colors whitespace-nowrap',
                        isLeft &&
                          'shadow-[4px_0_10px_-2px_rgba(0,0,0,0.06)] border-r border-border/50',
                        isRight &&
                          'shadow-[-4px_0_10px_-2px_rgba(0,0,0,0.06)] border-l border-border/50',
                        col.className,
                      )}
                    >
                      {col.cell(row)}
                    </td>
                  );
                })}
              </tr>
            ))}
        </tbody>
      </table>
      {/* Outside the table so it spans the visible width, not the (possibly wider, scrolling)
          table: at phone width a centred empty state inside a wide row was cut off. */}
      {!isLoading && rows.length === 0 && (
        <div className="sticky left-0 bg-surface p-8 text-center">{emptyState}</div>
      )}
    </div>
  );
}
